import { SubmitHandler, useForm } from "react-hook-form";
import { useSpring } from "react-spring";
import * as yup from "yup";
import { colors, screenHues, screens } from "../constants/constants";
import { springConfigs } from "../constants/springConfigs";
import { useWindowSize } from "../hooks/useWindowSize";
import { OnCurtainStart } from "../types/types";
import {
  Arrows,
  LeftArrow,
  RightArrow,
} from "./ScreenLanding/components/Arrows";
import { Contour } from "./ScreenLanding/components/Contour";
import { IocoText } from "./ScreenLanding/components/IocoText";
import { PageNumber } from "./ScreenLanding/components/PageNumber";
import { SVGCircle } from "./ScreenMenu/components/SVGCircle";
import { Hamburger } from "./shared/Hamburger";
import { RectangleButton } from "./shared/RectangleButton";
import { ScreenBackground } from "./shared/ScreenBackground";
import { AnimatedPreorder } from "./shared/SVGComponents";
import { Video } from "./shared/Video";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMargins } from "../hooks/hooks";

const schema = yup
  .object({
    email: yup.string().email().required(),
  })
  .required();

export const ScreenPreorder = ({
  isCurtainActive,
  isCurtainIntro,
  onCurtainStart,
}: {
  isCurtainActive: boolean;
  isCurtainIntro: boolean;
  onCurtainStart: OnCurtainStart;
}) => {
  const mesh = [
    "radial-gradient(circle at 50% 0%, #3912AC 0, transparent 55%)",
    "linear-gradient(#3912AC -30%, #271442)",
  ];
  // console.log({ isCurtainActive });

  const background = `
            ${mesh[0]},
            ${mesh[1]}
        `;

  const isContentShown =
    !isCurtainActive || (isCurtainActive && isCurtainIntro);

  const { liftAsset } = useSpring({
    from: { liftAsset: "10%" },
    to: { liftAsset: "0%" },
    config: {
      tension: 180,
      friction: 120,
      mass: 10,
      precision: 0.00001,
    },
  });

  const { opacity } = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: springConfigs.assetAppears,
  });

  type Inputs = {
    email: string;
  };

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Inputs>({ resolver: yupResolver(schema) });

  const onSubmit: SubmitHandler<Inputs> = (data) => {
    // TODO SEND DATA
    console.log(data);
  };

  const { w, h, horizontal, vertical } = {
    ...useWindowSize(),
  };
  const { screenContentVerticalPadding, screenContentHorizontalPadding } =
    useMargins();

  return (
    <>
      <ScreenBackground
        {...{
          background,
          screenName: screens.preorder,
          gridArea: "AppContentContainer",
        }}
      />
      <Contour {...{ gridArea: "AppContentContainer" }} />

      {isContentShown && (
        <>
          <form
            onSubmit={handleSubmit(onSubmit)}
            style={{
              gridArea: "AppContentContainer",
              placeSelf: "center",
              display: "grid",
              color: "#FFFFFF",
              zIndex: 1,
              // padding: 0,
            }}
          >
            {/* include validation with required or other standard HTML validation rules */}
            {horizontal && (
              <>
                <input
                  className="text-input"
                  {...register("email", { required: true })}
                  style={{
                    width: w(25.7),
                    border: `${w(0.202)} solid #e31e53`,
                    padding: `${h(1)} ${h(2)}`,
                  }}
                  placeholder="my email"
                />
                <div {...{ style: { marginBottom: h(2) } }}></div>
                <RectangleButton
                  {...{
                    text: "PREORDER",
                    width: w(25.7),
                    onClick: () => {
                      handleSubmit(onSubmit)();
                    },
                  }}
                />
              </>
            )}

            {/* errors will return when field validation fails  */}
            <div
              id="error-email"
              className=""
              {...{
                style: {
                  textAlign: "center",
                  maxHeight: h(8),
                  height: h(8),
                  display: "grid",
                  placeItems: "center",
                },
              }}
            >
              {errors.email && <div style={{}}>Please enter a valid email</div>}
            </div>
          </form>

          {horizontal && (
            <>
              {" "}
              <Video
                {...{
                  containerStyle: {
                    gridArea: "AppContentContainer",
                    placeSelf: "center start",
                    marginLeft: "-22%",
                    width: "64%",
                  },
                  videoStyle: {
                    marginTop: liftAsset,
                    opacity,
                    transform: "scaleX(-1) scaleY(-1) rotate(0deg)",
                    filter: screenHues.preorder,
                  },
                }}
              />
              <Video
                {...{
                  containerStyle: {
                    gridArea: "AppContentContainer",
                    placeSelf: "center end",
                    marginRight: "-22%",
                    width: "64%",
                  },
                  videoStartSecond: 4,
                  videoStyle: {
                    marginTop: liftAsset,
                    opacity,
                    transform: "scaleX(1) scaleY(1) rotate(0deg)",
                    filter: screenHues.preorder,
                  },
                }}
              />{" "}
            </>
          )}
          {vertical && (
            <Video
              {...{
                containerStyle: {
                  width: "130%",
                  height: "130%",
                  marginTop: horizontal ? "" : "20%",
                  gridArea: "AppContentContainer",
                },
                videoStyle: {
                  width: horizontal ? "64%" : "150%",
                  placeSelf: "center ",
                  marginTop: liftAsset,
                  opacity,
                  filter: screenHues.preorder,
                },
              }}
            />
          )}
          <div
            id="contentGrid"
            className=""
            {...{
              style: {
                gridArea: "AppContentContainer",
                display: "grid",
                padding: `${screenContentVerticalPadding} ${screenContentHorizontalPadding}`, // start with first line
                gridTemplateColumns: `repeat(${
                  horizontal ? 7 : 4
                }, minmax(0, 1fr))`, // https://stackoverflow.com/questions/43311943/prevent-content-from-expanding-grid-items
                gridTemplateRows: "repeat(11, minmax(0, 1fr))", // https://stackoverflow.com/questions/43311943/prevent-content-from-expanding-grid-items
                gap: h(1),
              },
            }}
          >
            <IocoText
              {...{
                onCurtainStart,
                gridArea: horizontal ? "1/1" : "1/1", // second row first colum
              }}
            />
            <Hamburger
              {...{
                onCurtainStart,
                gridArea: horizontal ? "1/7/1/8" : "1/-2/2/-1",
              }}
            />
            {horizontal && (
              <Arrows
                {...{
                  gridArea: "5/1/7/2",
                  onCurtainStart,
                  screenLeft: "media",
                  screenRight: "landing",
                }}
              />
            )}
            {vertical && (
              <div
                id=""
                {...{
                  style: {
                    gridArea: "-3/1/-1/2",
                    display: "grid",
                    placeSelf: "center start",
                    width: "70%",
                    gap: h(1),
                    zIndex: 9999,
                  },
                }}
              >
                <LeftArrow
                  {...{
                    onCurtainStart,
                    screenLeft: "media",
                    screenRight: "landing",
                    containerStyle: {
                      width: "87.5%", // matain a 7/8th ratio
                      placeSelf: "start",
                    },
                  }}
                />
                <RightArrow
                  {...{
                    onCurtainStart,
                    screenLeft: "media",
                    screenRight: "landing",
                  }}
                />
              </div>
            )}

            <PageNumber
              {...{
                gridArea: horizontal ? "5/7/7/8" : "2/1/3/4",
                pageNumber: "05",
              }}
            />

            {vertical && (
              <>
                <input
                  className="text-input"
                  {...register("email", { required: true })}
                  style={{
                    gridArea: "-3/2/-2/-1",
                    // width: w(25.7),
                    border: `1.5px solid #e31e53`,
                    padding: `${h(1)} ${h(2)}`,
                  }}
                  placeholder="my email"
                />
                <RectangleButton
                  {...{
                    gridArea: "-2/2/-1/-1",
                    text: "PREORDER",
                    width: horizontal ? "75%" : "100%",
                    onClick: () => {
                      handleSubmit(onSubmit)();
                    },
                  }}
                />
              </>
            )}

            <div
              id="animated-svg"
              className=""
              {...{
                style: {
                  gridArea: horizontal ? "-2/6/-1/8" : "4/1/4/3",
                  color: colors.pink,
                  fontSize: h(1),
                  letterSpacing: w(1),
                  placeSelf: horizontal ? "end" : "start",
                  zIndex: 999,
                  display: "grid",
                  gridTemplate: `
                    "a"
                    "b"
                  `,
                  gap: h(2),
                },
              }}
            >
              <SVGCircle
                {...{
                  active: true,
                  appearSlideUp: false,
                  style: {
                    margin: "auto",
                  },
                  containerStyle: {
                    // gridArea: "5/-3",
                    placeSelf: horizontal ? "end" : "start",
                    padding: 0,
                  },
                  svgComponent: (
                    <AnimatedPreorder
                      {...{ active: false, slideComponentUp: false }}
                    />
                  ),
                }}
              />
              <div
                id=""
                className=""
                {...{ style: { placeSelf: "end", marginRight: `-${w(1)}` } }}
              >
                IOCO PREORDER
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};
